import {useState, useEffect} from "react";
import * as Yup from "yup";
import {Form, Formik} from "formik";

import {AvailableBalance, requestCreditWallet} from "../../utils/creditService";
import {getWhitelists, WhitelistWallet} from "../../utils/whitelistService";

import FormSubmitValidation from "../form-elements/FormSubmitValidation";
import ModalFormWrapper from "../common/ModalFormWrapper";
import FormGroupErrorText from "../form-elements/FormGroupErrorText";
import Button from "../common/Button";
import {TruncateWalletAddress} from "../../functions/TruncateWalletAddress";
import styles from "../../styles/HomeDrawCreditFormWallet.module.scss";

interface HomeDrawCreditFormWalletProps {
  onCloseModal: () => void;
  handleBack: () => void;
  creditLine: AvailableBalance
}

const HomeDrawCreditFormWalletSchema = (creditLine: AvailableBalance) => Yup.object().shape({
  amount: Yup.number().required('Please enter an amount to proceed.').max(creditLine.available, 'Amount cannot exceed your available credit'),
  referenceIds: Yup.string().notRequired(),
  selectedWallet: Yup.string().required('Please select a wallet address to proceed.')
})

function HomeDrawCreditFormWallet(
  {
    onCloseModal,
    handleBack,
    creditLine,
  }: HomeDrawCreditFormWalletProps) {

  // Use creditLine.id from props instead of context to ensure correct ID from URL
  const [whitelistedWallets, setWhitelistedWallets] = useState<WhitelistWallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState<{
    amount: number;
    referenceIds: string;
    selectedWallet: string;
  } | null>(null);

  const [formData] = useState<{
    amount: number;
    referenceIds: string;
    selectedWallet: string;
  }>({
    amount: 0,
    referenceIds: "",
    selectedWallet: ""
  })

  useEffect(() => {
    const fetchWhitelistedWallets = async () => {
      try {
        const wallets = await getWhitelists();
        setWhitelistedWallets(wallets);
      } catch (error) {
        console.error('Error fetching whitelisted wallets:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWhitelistedWallets();
  }, []);

  // Filter wallets to only show compliant ones
  const compliantWallets = whitelistedWallets.filter(wallet => wallet.is_compliant);
  const pendingWallets = whitelistedWallets.filter(wallet => !wallet.is_compliant);
  const pendingCount = pendingWallets.length;

  // Calculate the available credit balance
  const availableCredit = creditLine.available;
  const currency = creditLine.currency.toUpperCase();


  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setConfirmationData(null);
  };



  return (
    <Formik
      initialValues={formData}
      validationSchema={HomeDrawCreditFormWalletSchema(creditLine)}
      validateOnMount={true}
      validateOnChange={true}
      onSubmit={async (values, { setSubmitting, setStatus }) => {
        if (!showConfirmation) {
          // First submission - show confirmation
          setConfirmationData({
            amount: values.amount as number,
            referenceIds: values.referenceIds,
            selectedWallet: values.selectedWallet
          });
          setShowConfirmation(true);
          return;
        }
        
        // Actual submission after confirmation
        setSubmitting(true);
        try {
          await requestCreditWallet({
            amount: values.amount as number,
            wallet_address: values.selectedWallet,
            selectedCreditLine: creditLine.id,
            referenceIds: values.referenceIds
          });
          setStatus("submitted");
          // refresh dashboard
        } catch (error) {
          setStatus("error");
        }
        setSubmitting(false);
      }}
    >
      {({ isSubmitting, values, status, errors, isValid, touched, setFieldValue, submitForm }) => {
        const drawCredit = Number(values.amount) || 0;
        const remainingBalance = availableCredit - drawCredit;
        
        return (
          <>
            {status === "submitted" ? (
              <div className="c-modal-form-wrapper">
                <FormSubmitValidation type="success">
                  <h2 className="h4">
                    You transferred ${values.amount} {currency}
                  </h2>
                  <p>
                    Credit will be deposited to:
                  </p>
                  <p className={styles.walletAddress}>
                    {values.selectedWallet}
                  </p>
                </FormSubmitValidation>
                <div className="c-modal-footer">
                  <Button
                    text="Done"
                    onClick={onCloseModal}
                    btnSize="lg"
                  />
                </div>
              </div>
            ) : showConfirmation && confirmationData ? (
              <div className="c-modal-form-wrapper">
                <ModalFormWrapper onCloseModal={onCloseModal} headerTitle="Confirm Transfer" onClick={handleCancelConfirmation}>
                  <div className={styles.formContainer}>
                    <div className={styles.confirmationContainer}>
                      <div className={styles.confirmationHeader}>
                        <h3 className={styles.confirmationTitle}>
                          Are you sure you want to send funds to this address?
                        </h3>
                        <p className={styles.confirmationSubtitle}>
                          Please review the transaction details below before confirming.
                        </p>
                      </div>

                      <div className={styles.transactionDetails}>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>Amount:</span>
                          <span className={styles.detailValue}>
                            ${confirmationData.amount.toLocaleString()} {currency}
                          </span>
                        </div>
                        
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>Destination Wallet:</span>
                          <div className={styles.walletInfo}>
                            <span className={styles.walletAddressConfirm}>
                              {confirmationData.selectedWallet}
                            </span>
                          </div>
                        </div>

                        {confirmationData.referenceIds && (
                          <div className={styles.detailRow}>
                            <span className={styles.detailLabel}>Reference ID(s):</span>
                            <span className={styles.detailValue}>
                              {confirmationData.referenceIds}
                            </span>
                          </div>
                        )}

                      </div>

                      <div className={styles.confirmationWarning}>
                        <p className={styles.warningText}>
                          This action cannot be undone. Please verify the wallet address is correct before proceeding.
                        </p>
                      </div>

                      <div className={styles.confirmationActions}>
                        <Button
                          text="Cancel"
                          onClick={handleCancelConfirmation}
                          btnSize="lg"
                          className={styles.cancelButton}
                        />
                        <Button
                          text={`Confirm & Send ${currency}`}
                          onClick={() => {
                            // Trigger form submission with confirmation using Formik's submitForm
                            submitForm();
                          }}
                          btnSize="lg"
                          className={styles.confirmButton}
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  </div>
                </ModalFormWrapper>
              </div>
            ) : (
              <Form>
                <ModalFormWrapper onCloseModal={onCloseModal} headerTitle="Transfer to Wallet" onClick={handleBack}>
                  <div className={styles.formContainer}>
                    {/* Amount Input */}
                    <div className={styles.formField}>
                      <label className={styles.fieldLabel}>Amount</label>
                      <div className={styles.amountInputWrapper}>
                        <input
                          type="number"
                          name="amount"
                          value={values.amount || ''}
                          onChange={(e) => setFieldValue('amount', e.target.value)}
                          placeholder="0"
                          className={styles.amountInput}
                          disabled={isSubmitting}
                        />
                        <div className={styles.currencyBadge}>
                          <div className={styles.currencyIcon}>$</div>
                          <span className={styles.currencyText}>{currency}</span>
                        </div>
                    </div>
                    {errors.amount && touched.amount && (
                      <FormGroupErrorText text={errors.amount} />
                    )}
                  </div>

                  {/* Reference IDs Input */}
                  <div className={styles.formField}>
                    <label className={styles.fieldLabel}>Reference Id(s)</label>
                    <input
                      type="text"
                      name="referenceIds"
                      value={values.referenceIds}
                      onChange={(e) => setFieldValue('referenceIds', e.target.value)}
                      placeholder="Enter reference IDs separated by commas"
                      className={styles.textInput}
                      disabled={isSubmitting}
                    />
                    <p className={styles.helpText}>
                      Separate multiple reference IDs with commas (e.g., REF001, REF002)
                    </p>
                  </div>

                  {/* Balance Summary */}
                  <div className={styles.balanceSummary}>
                    <div className={styles.balanceRow}>
                      <span className={styles.balanceLabel}>Available credit</span>
                      <span className={styles.balanceValue}>
                        ${availableCredit.toLocaleString()} {currency}
                      </span>
                    </div>
                    <div className={styles.balanceRow}>
                      <span className={styles.balanceLabel}>Draw credit</span>
                      <span className={`${styles.balanceValue} ${drawCredit > 0 ? styles.drawAmount : styles.inactiveAmount}`}>
                        -{drawCredit.toLocaleString()} {currency}
                      </span>
                    </div>
                    <div className={styles.balanceDivider}></div>
                    <div className={styles.balanceRow}>
                      <span className={styles.balanceTotalLabel}>Available credit balance</span>
                      <span className={styles.balanceTotalValue}>
                        ${remainingBalance.toLocaleString()} {currency}
                      </span>
                    </div>
                  </div>

                  {/* Wallet Address Selection */}
                  <div className={styles.formField}>
                    <div className={styles.labelRow}>
                      <label className={styles.fieldLabel}>Wallet Address</label>
                      {pendingCount > 0 && (
                        <span className={styles.complianceNote}>
                          (there are {pendingCount} wallet{pendingCount !== 1 ? 's' : ''} pending compliance)
                        </span>
                      )}
                    </div>
                    
                    {loading ? (
                      <div className={styles.loadingMessage}>
                        Loading whitelisted wallets...
                      </div>
                    ) : compliantWallets.length === 0 ? (
                      <div className={styles.noWalletsWarning}>
                        <div className={styles.warningIcon}>⚠</div>
                        <div className={styles.warningContent}>
                          <p className={styles.warningTitle}>
                            {whitelistedWallets.length === 0 ? 'No whitelisted wallets found' : 'No compliant wallets available'}
                          </p>
                          <p className={styles.warningText}>
                            {whitelistedWallets.length === 0 
                              ? 'Please add a wallet to your whitelist first.' 
                              : 'All your whitelisted wallets are pending compliance review.'}
                          </p>
                          <button className={styles.addWalletLink} type="button">
                            Add Wallet to Whitelist
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.selectWrapper}>
                        <select
                          name="selectedWallet"
                          value={values.selectedWallet}
                          onChange={(e) => setFieldValue('selectedWallet', e.target.value)}
                          className={`${styles.selectField} ${errors.selectedWallet && touched.selectedWallet ? styles.selectError : ''}`}
                          disabled={isSubmitting}
                        >
                          <option value="">Select a wallet address</option>
                          {compliantWallets.map((wallet) => (
                            <option key={wallet.id} value={wallet.wallet_address}>
                              {wallet.label} - {TruncateWalletAddress({address: wallet.wallet_address})}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {errors.selectedWallet && touched.selectedWallet && (
                      <FormGroupErrorText text={errors.selectedWallet} />
                    )}
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    text={`Send ${currency}`}
                    btnSize="lg"
                    disabled={isSubmitting || !isValid || compliantWallets.length === 0 || !values.amount}
                    className={styles.submitButton}
                  />
                </div>
              </ModalFormWrapper>
            </Form>
          )}
        </>
      );
    }}
  </Formik>
  );
}

export default HomeDrawCreditFormWallet;